import {
  useState,
  useEffect,
  ReactNode,
} from "react";
import axios from "axios";
import { BackendAuthService } from "../services/backendAuth";
import { useNavigate } from "react-router-dom";
import { User } from './authUtils';
import { AuthContext } from './authContext';
import { buildApiUrl } from '../config/api';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = BackendAuthService.getAuthToken();
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, []);

  const checkAuth = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if user is authenticated via backend SSO
      if (BackendAuthService.isAuthenticated()) {
        const currentUser = BackendAuthService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
        } else {
          setUser(null);
        }
      } else {
        // Fallback to check server auth status
        try {
          const response = await axios.get(buildApiUrl("auth/status"));
          const { authenticated, user: userData } = response.data;
          if (authenticated && userData) {
            setUser(userData);
          } else {
            setUser(null);
          }
        } catch (err) {
          console.error("Server auth check failed:", err);
          setUser(null);
        }
      }
    } catch (err) {
      console.error("Auth check failed:", err);
      setUser(null);
      setError("Authentication check failed");
    } finally {
      setLoading(false);
    }
  };

  const login = async () => {
    try {
      setLoading(true);
      setError(null);
      await BackendAuthService.loginWithSSO();
      // The redirect will happen automatically
    } catch (err: unknown) {
      console.error("Login failed:", err);
      const errorMessage = err instanceof Error ? err.message : "Login failed";
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await BackendAuthService.logout();
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      setUser(null);
      delete axios.defaults.headers.common["Authorization"];
      navigate("/login");
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, loading, error, login, logout, checkAuth }}
    >
      {children}
    </AuthContext.Provider>
  );
}


